import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { HiCheck, HiOutlineXMark } from 'react-icons/hi2'
import { twMerge } from 'tailwind-merge'

import TextInput, { type TextInputProps } from '@/components/common/TextInput'

interface ComboboxProps<T> {
  query: string
  options: T[]
  placeholder: string
  showInput?: boolean
  selectedIds: string[]
  itemDisplayFn: (item: T) => string | React.ReactNode
  itemIdFn: (item: T) => string
  onAdd: (item: T) => void
  onRemove: (item: T) => void
  setQuery: (query: string) => void
}

interface InputProps extends TextInputProps {
  setQuery?: (query: string) => void
}

interface ItemProps<T> {
  option: T
  selected?: boolean
  highlighted?: boolean
  itemDisplayFn: (item: T) => string | React.ReactNode
  onToggle: (item: T) => void
}

const ItemInner = <T,>({
  option,
  selected,
  highlighted,
  itemDisplayFn,
  onToggle,
}: ItemProps<T>) => {
  const display = itemDisplayFn(option)
  return (
    <button
      type="button"
      onClick={() => onToggle(option)}
      className={twMerge(
        'text-left text-sm w-full py-1.5 px-2 flex items-start justify-between gap-x-1.5',
        'rounded-md transition-colors',
        'hover:bg-primary-100 dark:hover:bg-primary-800',
        highlighted && 'bg-primary-100 dark:bg-primary-800',
        selected &&
          'bg-accent-50 dark:bg-accent-900/20 hover:bg-accent-100 dark:hover:bg-accent-900/30',
      )}
    >
      <span
        className={twMerge(
          'min-w-0',
          selected
            ? 'text-accent-700 dark:text-accent-300 font-medium'
            : 'text-primary-900 dark:text-primary-300',
        )}
      >
        {display}
      </span>
      <HiCheck
        className={twMerge(
          'flex-shrink-0 w-3.5 h-3.5 mt-0.5',
          selected ? 'text-accent-500' : 'invisible',
        )}
      />
    </button>
  )
}

const Item = memo(
  ItemInner,
  (prev, next) =>
    prev.option === next.option &&
    prev.selected === next.selected &&
    prev.highlighted === next.highlighted &&
    prev.onToggle === next.onToggle,
) as typeof ItemInner

const Input = ({
  setQuery,
  ref,
  ...props
}: InputProps & { ref?: React.Ref<HTMLInputElement> }) => {
  const showClear = props.value && String(props.value).length > 0
  return (
    <div className="relative">
      <TextInput
        ref={ref}
        className={twMerge(
          'h-[30px] px-2 rounded-[4px]',
          setQuery ? 'pr-6' : '',
        )}
        {...props}
      />
      {showClear && setQuery && (
        <button
          type="button"
          onClick={() => setQuery('')}
          className="absolute right-[6px] top-0 flex items-center justify-center h-full hover:opacity-70"
        >
          <HiOutlineXMark className="w-4 h-4 text-primary-500 dark:text-primary-400" />
        </button>
      )}
    </div>
  )
}

const Combobox = <T,>({
  query,
  options,
  placeholder,
  showInput = true,
  selectedIds,
  itemDisplayFn,
  itemIdFn,
  onAdd,
  onRemove,
  setQuery,
}: ComboboxProps<T>) => {
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds])
  const onAddRef = useRef(onAdd)
  const onRemoveRef = useRef(onRemove)
  onAddRef.current = onAdd
  onRemoveRef.current = onRemove
  const wrapperDiv = useRef<HTMLDivElement>(null)
  const optionsDiv = useRef<HTMLDivElement>(null)
  const hasToggledRef = useRef(false)

  const [index, setIndex] = useState<number | null>(null)
  const fadeDiv = useRef<HTMLDivElement>(null)

  const handleQueryChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setQuery(e.target.value)
    },
    [setQuery],
  )

  const updateFade = useCallback(() => {
    const node = fadeDiv.current
    if (!node) return
    const el = optionsDiv.current
    if (!el) return
    const hasMore =
      Math.abs(el.scrollHeight - el.scrollTop - el.clientHeight) > 1
    node.style.opacity = hasMore ? '1' : '0'
  }, [])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // Nothing to navigate/commit when the list is empty (e.g. an idle field
    // before the user has typed) — guard so ArrowDown→Enter can't index into
    // an empty array and call onAdd/onRemove with `undefined`.
    if (options.length === 0) return
    if (e.key === 'ArrowDown') {
      setIndex(Math.min(Math.max((index ?? -1) + 1, 0), options.length - 1))
    } else if (e.key === 'ArrowUp') {
      setIndex(
        Math.max(Math.min(index ?? options.length, options.length) - 1, 0),
      )
    } else if (e.key === 'Enter' && index !== null) {
      const option = options[index]
      if (option) handleItemClick(option)
    }
  }

  const handleBlur = () => {
    setIndex(null)
  }

  const handleWrapperBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (e.currentTarget.contains(e.relatedTarget as Node)) return
    // Focus left the combobox — clear search if user toggled a selection
    if (hasToggledRef.current && query.length > 0) {
      setQuery('')
    }
    hasToggledRef.current = false
    if (fadeDiv.current) fadeDiv.current.style.opacity = '0'
  }

  const selectedSetRef = useRef(selectedSet)
  selectedSetRef.current = selectedSet

  const handleItemClick = useCallback(
    (item: T) => {
      hasToggledRef.current = true
      const isSelected = selectedSetRef.current.has(itemIdFn(item))
      const toggle = isSelected ? onRemoveRef.current : onAddRef.current
      toggle(item)
    },
    [itemIdFn],
  )

  useEffect(() => {
    setIndex(null)
    updateFade()
  }, [options.length])

  useEffect(() => {
    const el = optionsDiv.current
    if (!el) return
    const observer = new ResizeObserver(() => updateFade())
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (index !== null && optionsDiv.current) {
      const element = optionsDiv.current.children[index] as HTMLElement
      if (element) {
        element.scrollIntoView({ behavior: 'auto', block: 'nearest' })
      }
    }
  }, [index])

  return (
    <div
      ref={wrapperDiv}
      onBlur={handleWrapperBlur}
      className="relative flex flex-col w-full"
    >
      <div className={twMerge(showInput ? '' : 'hidden')}>
        <Input
          placeholder={placeholder}
          value={query}
          onBlur={handleBlur}
          onChange={handleQueryChange}
          onKeyDown={handleKeyDown}
          setQuery={setQuery}
        />
      </div>
      <div
        ref={optionsDiv}
        className={twMerge(
          'flex flex-col gap-0.5 overflow-y-auto',
          options.length > 0 ? 'max-h-60' : '',
          options.length > 0 && showInput ? 'pt-2' : '',
        )}
        onScroll={updateFade}
      >
        {options.map((option, idx) => {
          const isSelected = selectedSet.has(itemIdFn(option))
          return (
            <Item
              key={itemIdFn(option)}
              option={option}
              selected={isSelected}
              highlighted={index === idx}
              itemDisplayFn={itemDisplayFn}
              onToggle={handleItemClick}
            />
          )
        })}
      </div>
      <div
        ref={fadeDiv}
        className="absolute bottom-0 pointer-events-none h-10 w-full from-transparent to-white dark:to-primary-900 bg-gradient-to-b"
        style={{ opacity: 0 }}
      />
    </div>
  )
}

Combobox.displayName = 'Combobox'
Input.displayName = 'Input'
ItemInner.displayName = 'Combobox.Item'

export { Combobox, Input, Item }
